// @flow
import { useState } from 'react';
import createCoachAccount from '../../api/createCoachAccount';
import createAthleteAccount from '../../api/createAthleteAccount';
import useAuthentication from './useAuthentication';
import SegmentService from '../../../lib/segment/SegmentService';
import { SEGMENT_EVENTS } from '../../../lib/segment/SegmentEvents';
import { getConsentFlags } from '../../../lib/consent/oneTrustConsent';

type CoachUser = {
    fullName: string,
    email: string,
    password: string,
    phoneNumber: string,
    country: string,
    organization: string,
    expectedAccountSize: string,
    token: string,
    hasAgreedToSMS: boolean,
    accountCreationLocation?: string
};

export default function useAccountCreation(resetCaptcha?: () => void) {
    const [creatingInProcess, setCreatingInProcess] = useState(false);
    const [creatingComplete, setCreatingComplete] = useState(null);
    const [serverError, setServerError] = useState(false);
    const [serverErrorMessages, setServerErrorMessages] = useState({});

    const { loginUser } = useAuthentication();

    const createNewAthleteAccount = async (name: string, email: string, password: string, token: string) => {
        setCreatingComplete(null);
        setCreatingInProcess(true);

        const newUser = {
            accountCreationLocation: 'account',
            full_name: name,
            email,
            password,
            token
        };

        const response = await createAthleteAccount(newUser);
        handleCreateAccountResponse(response);
        return response;
    };

    const createNewCoachAccount = async (coachUser: CoachUser) => {
        setCreatingComplete(null);
        setCreatingInProcess(true);

        const response = await createCoachAccount(coachUser);
        handleCreateCoachAccountResponse(response, coachUser);
        return response;
    };

    const handleCreateAccountResponse = (response: any) => {
        if (response.session_id) {
            handleSuccessfulResponse(response);
        } else if (Array.isArray(response)) {
            handleUserInputError(response);
        } else {
            handleServerError();
        }

        setCreatingInProcess(false);
    };

    const handleCreateCoachAccountResponse = (response: any, coachUser: CoachUser) => {
        if (response.session_id) {
            handleSuccessfulResponse(response);
            const { performanceAllowed, targetingAllowed } = getConsentFlags(window.OnetrustActiveGroups);

            if (performanceAllowed) {
                SegmentService.identify(response.id, coachUser.email);
                SegmentService.track(SEGMENT_EVENTS.CreateCoachAccount.key, {
                    formName: 'Coach Account Signup',
                    userType: 'Coach',
                    formData: {
                        fullName: coachUser.fullName,
                        email: coachUser.email,
                        phoneNumber: coachUser.phoneNumber,
                        country: coachUser.country,
                        organization: coachUser.organization,
                        expectedAccountSize: coachUser.expectedAccountSize
                    }
                });
            }

            if (targetingAllowed) {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: 'coach_trial_created',
                    coach_id: response.id,
                    plan_type: 'trial',
                    signup_source: coachUser.accountCreationLocation || 'account',
                    referrer: document.referrer || '',
                    utm_source: SegmentService.params?.utm_source || '',
                    utm_medium: SegmentService.params?.utm_medium || '',
                    utm_campaign: SegmentService.params?.utm_campaign || '',
                    utm_term: SegmentService.params?.utm_term || '',
                    utm_content: SegmentService.params?.utm_content || ''
                });
            }
        } else if (Array.isArray(response)) {
            handleUserInputError(response);
        } else {
            handleServerError();
        }

        setCreatingInProcess(false);
    };

    const handleSuccessfulResponse = async (response: any) => {
        await loginUser(response.session_id);
        setCreatingComplete(true);
    };

    const handleUserInputError = (response: Array) => {
        setCreatingComplete(false);
        const errorsToDisplay = convertServerResponse(response);
        setServerErrorMessages(errorsToDisplay);
        if (resetCaptcha) {
            resetCaptcha();
        }
    };

    const handleServerError = () => {
        setServerError(true);
        setCreatingComplete(false);
        if (resetCaptcha) {
            resetCaptcha();
        }
    };

    const convertServerResponse = (errorMessages: Array) =>
        errorMessages.reduce((acc, error) => {
            const errorKeys = Object.keys(error);
            const errorValues = Object.values(error);

            if (errorKeys.length) {
                if (!acc[errorKeys[0]]) {
                    acc[errorKeys[0]] = errorValues[0];
                }
            }
            return acc;
        }, {});

    return {
        createNewAthleteAccount,
        createNewCoachAccount,
        creatingInProcess,
        creatingComplete,
        setCreatingInProcess,
        setCreatingComplete,
        serverErrorMessages,
        serverError,
        setServerError
    };
}
